import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { POLES } from "@/lib/poles";
import {
  Plus, X, Edit3, Trash2, ClipboardList, ExternalLink, Copy, Send,
  ArrowUp, ArrowDown, MessageSquareText, Download, Users, Filter, Loader2,
} from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texte court" },
  { value: "textarea", label: "Texte long" },
  { value: "email", label: "Email" },
  { value: "number", label: "Nombre" },
  { value: "tel", label: "Téléphone" },
  { value: "url", label: "URL" },
  { value: "date", label: "Date" },
  { value: "select", label: "Liste déroulante" },
  { value: "radio", label: "Choix unique (radio)" },
  { value: "checkbox", label: "Cases à cocher (multi)" },
];
const NEEDS_OPTIONS = new Set(["select", "radio", "checkbox"]);

function emptyForm() {
  return {
    title: "",
    description: "",
    fields: [],
    success_message: "Merci ! Votre réponse a été enregistrée.",
    status: "published",
  };
}

function emptyField() {
  return { label: "Nouvelle question", type: "text", required: false, placeholder: "", options: [] };
}

// ============================== FIELD EDITOR ==============================

function FieldEditor({ field, idx, total, onChange, onMove, onRemove, onDuplicate }) {
  const needsOptions = NEEDS_OPTIONS.has(field.type);
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3" data-testid={`field-row-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-neutral-500">
          <MessageSquareText className="h-3 w-3 text-wine-500" /> Question {idx + 1}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" disabled={idx === 0} onClick={() => onMove(idx, -1)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100 disabled:opacity-30" title="Monter" data-testid={`field-up-${idx}`}><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={idx === total - 1} onClick={() => onMove(idx, 1)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100 disabled:opacity-30" title="Descendre" data-testid={`field-down-${idx}`}><ArrowDown className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => onDuplicate(idx)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Dupliquer" data-testid={`field-dup-${idx}`}><Copy className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => onRemove(idx)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`field-del-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="mt-2 grid gap-2 md:grid-cols-[1fr_180px]">
        <input value={field.label} onChange={(e) => onChange(idx, { label: e.target.value })} placeholder="Libellé de la question" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid={`field-label-${idx}`} />
        <select value={field.type} onChange={(e) => onChange(idx, { type: e.target.value })} className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid={`field-type-${idx}`}>
          {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto]">
        <input value={field.placeholder || ""} onChange={(e) => onChange(idx, { placeholder: e.target.value })} placeholder="Aide / placeholder (optionnel)" className="rounded-lg border border-neutral-200 px-3 py-2 text-xs" data-testid={`field-placeholder-${idx}`} />
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={!!field.required} onChange={(e) => onChange(idx, { required: e.target.checked })} className="accent-wine-500" data-testid={`field-required-${idx}`} />
          Obligatoire
        </label>
      </div>
      {needsOptions && (
        <div className="mt-2">
          <label className="text-[10px] font-bold uppercase text-neutral-500">Options (une par ligne)</label>
          <textarea
            value={(field.options || []).join("\n")}
            onChange={(e) => onChange(idx, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
            rows={3}
            placeholder={"Option 1\nOption 2\nOption 3"}
            className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-xs font-mono"
            data-testid={`field-options-${idx}`}
          />
        </div>
      )}
    </div>
  );
}

// ============================== SEND MODAL ==============================

function SendFormModal({ form, onClose }) {
  const [audience, setAudience] = useState({ audience_type: "members", categories: [], poles: [], langs: [], subject: "", intro_html: "" });
  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/membership-categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const togglePole = (id) => {
    const arr = audience.poles || [];
    setAudience({ ...audience, poles: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  const toggleCategory = (id) => {
    const arr = audience.categories || [];
    setAudience({ ...audience, categories: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  const toggleLang = (l) => {
    const arr = audience.langs || [];
    setAudience({ ...audience, langs: arr.includes(l) ? arr.filter((x) => x !== l) : [...arr, l] });
  };

  const doPreview = async () => {
    try {
      const r = await api.post(`/admin/forms/${form.id}/preview-audience`, audience);
      setPreview(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const doSend = async () => {
    if (!preview) { await doPreview(); }
    if (!window.confirm(`Envoyer ce formulaire à ${preview?.total ?? "?"} destinataires ?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/forms/${form.id}/send`, audience);
      toast.success("Envoi mis en file d'attente — vous pouvez fermer cette fenêtre.");
      setTimeout(onClose, 800);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="form-send-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
          <div>
            <h3 className="text-xl font-bold text-navy"><Send className="mr-2 inline h-5 w-5 text-wine-500" /> Envoyer « {form.title} »</h3>
            <p className="text-xs text-neutral-500">Ciblage par audience puis envoi groupé par lots de 50.</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase text-neutral-500">Type d&apos;audience</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
              {[
                { v: "members", l: "👥 Membres actifs" },
                { v: "prospects", l: "🌱 Prospects" },
                { v: "newsletter", l: "📰 Abonnés newsletter" },
                { v: "all", l: "🌐 Tous (membres + prospects)" },
              ].map((o) => (
                <button key={o.v} type="button" onClick={() => setAudience({ ...audience, audience_type: o.v })} className={`rounded-full border px-3 py-1.5 font-semibold ${audience.audience_type === o.v ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`send-audience-${o.v}`}>{o.l}</button>
              ))}
            </div>
          </div>

          {(audience.audience_type === "members" || audience.audience_type === "all") && categories.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase text-neutral-500">Catégories d&apos;adhésion (membres)</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {categories.map((c) => {
                  const active = (audience.categories || []).includes(c.id);
                  return <button key={c.id} type="button" onClick={() => toggleCategory(c.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`send-cat-${c.id}`}>{c.name || c.label || c.id}</button>;
                })}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] font-bold uppercase text-neutral-500">Pôles (membres &amp; prospects)</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {POLES.map((p) => {
                const active = (audience.poles || []).includes(p.id);
                return <button key={p.id} type="button" onClick={() => togglePole(p.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`send-pole-${p.id}`}>{p.label}</button>;
              })}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-neutral-500">Langues (filtre optionnel)</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {["fr", "en", "es", "it", "pt", "de"].map((l) => {
                const active = (audience.langs || []).includes(l);
                return <button key={l} type="button" onClick={() => toggleLang(l)} className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${active ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-wine-50"}`} data-testid={`send-lang-${l}`}>{l}</button>;
              })}
            </div>
          </div>

          <div>
            <input value={audience.subject} onChange={(e) => setAudience({ ...audience, subject: e.target.value })} placeholder={`Objet (par défaut : 📝 ${form.title})`} className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="send-subject" />
            <textarea rows={3} value={audience.intro_html} onChange={(e) => setAudience({ ...audience, intro_html: e.target.value })} placeholder="Mot d'intro HTML (optionnel). Variables {{first_name}} {{last_name}}." className="mt-2 w-full rounded-lg border border-neutral-200 px-3 py-2 text-xs font-mono" data-testid="send-intro" />
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-neutral-50 p-3">
            <button type="button" onClick={doPreview} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-bold hover:bg-neutral-100" data-testid="send-preview-btn">
              <Filter className="h-3 w-3" /> Calculer l&apos;audience
            </button>
            {preview && (
              <div className="text-xs text-neutral-700" data-testid="send-preview-result">
                <strong className="text-wine-700">{preview.total}</strong> destinataires uniques
                {Object.keys(preview.by_lang || {}).length > 0 && (
                  <span> — par langue : {Object.entries(preview.by_lang).map(([l, n]) => `${l}: ${n}`).join(", ")}</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
          <button type="button" onClick={onClose} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
          <button type="button" disabled={sending} onClick={doSend} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="send-confirm">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer maintenant
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================== RESPONSES MODAL ==============================

function ResponsesModal({ form, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/forms/${form.id}/responses`)
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [form.id]);

  const fields = form.fields || [];
  const exportCSV = () => {
    const headers = ["submitted_at", "respondent_email", "respondent_name", ...fields.map((f) => f.label)];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const a = r.answers || {};
      const row = [r.submitted_at || "", r.respondent_email || "", r.respondent_name || "", ...fields.map((f) => a[f.id] ?? "")];
      lines.push(row.map((v) => `"${String(Array.isArray(v) ? v.join("; ") : v).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reponses-${form.slug}.csv`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-6xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="form-responses-modal">
        <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
          <div>
            <h3 className="text-xl font-bold text-navy">{form.title}</h3>
            <p className="text-xs text-neutral-500">{rows.length} réponse{rows.length > 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="form-export-csv"><Download className="h-3 w-3" /> Export CSV</button>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-100">
          <table className="w-full min-w-[800px] text-xs">
            <thead className="bg-wine-50/40 text-[10px] uppercase text-neutral-500">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Répondant</th>
                {fields.map((f) => <th key={f.id} className="px-3 py-2 text-left">{f.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={2 + fields.length} className="px-3 py-6 text-center text-neutral-500"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={2 + fields.length} className="px-3 py-6 text-center text-neutral-500">Aucune réponse</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-neutral-100">
                  <td className="px-3 py-2 text-neutral-500">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2"><div className="font-semibold">{r.respondent_name || "—"}</div><div className="text-[10px] text-neutral-500">{r.respondent_email || ""}</div></td>
                  {fields.map((f) => {
                    const v = r.answers?.[f.id];
                    return <td key={f.id} className="max-w-xs truncate px-3 py-2 text-neutral-700">{Array.isArray(v) ? v.join(", ") : (v ?? "—")}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================== MAIN PAGE ==============================

export default function AdminForms() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [sendTarget, setSendTarget] = useState(null);
  const [responsesTarget, setResponsesTarget] = useState(null);

  const load = useCallback(() => api.get("/admin/forms").then((r) => setItems(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (f) => { setEdit(f); setForm({ ...emptyForm(), ...f, fields: f.fields || [] }); setOpen(true); };
  const close = () => setOpen(false);

  const addField = () => setForm((f) => ({ ...f, fields: [...(f.fields || []), emptyField()] }));
  const updateField = (idx, patch) => setForm((f) => ({ ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x) }));
  const moveField = (idx, dir) => setForm((f) => {
    const arr = [...f.fields];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...f, fields: arr };
  });
  const removeField = (idx) => setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  const duplicateField = (idx) => setForm((f) => {
    const arr = [...f.fields];
    const clone = { ...arr[idx], id: undefined };
    arr.splice(idx + 1, 0, clone);
    return { ...f, fields: arr };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Le titre est obligatoire");
    if ((form.fields || []).length === 0) return toast.error("Ajoutez au moins une question");
    try {
      if (edit) await api.patch(`/admin/forms/${edit.id}`, form);
      else await api.post("/admin/forms", form);
      toast.success(edit ? "Formulaire mis à jour" : "Formulaire créé");
      close(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer "${f.title}" ?`)) return;
    try { await api.delete(`/admin/forms/${f.id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const copyLink = (f) => {
    const url = `${window.location.origin}/formulaires/${f.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><ClipboardList className="mr-2 inline h-7 w-7 text-wine-500" />Formulaires</h1>
          <p className="mt-1 text-neutral-600">Créez des formulaires personnalisés et envoyez-les par email à des segments ciblés (membres, prospects, par pôle ou catégorie).</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="form-new-btn">
          <Plus className="h-4 w-4" />Nouveau formulaire
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Titre</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Champs</th>
              <th className="px-4 py-3 text-left">Réponses</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">Aucun formulaire</td></tr>}
            {items.map((f) => (
              <tr key={f.id} className="border-t border-neutral-100" data-testid={`admin-form-row-${f.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{f.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">/{f.slug}</td>
                <td className="px-4 py-3 text-neutral-700">{(f.fields || []).length}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-sm font-semibold text-wine-700"><Users className="h-3.5 w-3.5" />{f.total_responses || 0}</span></td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${f.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{f.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setResponsesTarget(f)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Voir les réponses" data-testid={`form-responses-${f.id}`}><MessageSquareText className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setSendTarget(f)} className="grid h-7 w-7 place-items-center rounded-full text-wine-600 hover:bg-wine-50" title="Envoyer par email" data-testid={`form-send-${f.id}`}><Send className="h-3.5 w-3.5" /></button>
                    <button onClick={() => copyLink(f)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Copier le lien" data-testid={`form-copy-${f.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    <a href={`/formulaires/${f.slug}`} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Voir public" data-testid={`form-view-${f.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>
                    <button onClick={() => openEdit(f)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Modifier" data-testid={`form-edit-${f.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(f)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`form-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={close}>
          <form onSubmit={submit} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-form-modal">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
              <h3 className="text-xl font-bold text-navy">{edit ? <><Edit3 className="mr-1 inline h-5 w-5" /> Modifier le formulaire</> : <><Plus className="mr-1 inline h-5 w-5" /> Nouveau formulaire</>}</h3>
              <button type="button" onClick={close} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 space-y-3">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du formulaire" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-form-title" />
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description / consignes (affichée en haut)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-form-desc" />
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Statut</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-form-status">
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Message de remerciement</label>
                  <input value={form.success_message} onChange={(e) => setForm({ ...form, success_message: e.target.value })} placeholder="Merci ! Votre réponse a été enregistrée." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-form-success" />
                </div>
              </div>

              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-navy">Questions ({(form.fields || []).length})</h4>
                  <button type="button" onClick={addField} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="add-field-btn"><Plus className="h-3 w-3" /> Ajouter une question</button>
                </div>
                <div className="mt-3 space-y-2">
                  {(form.fields || []).map((f, idx) => (
                    <FieldEditor key={idx} field={f} idx={idx} total={form.fields.length} onChange={updateField} onMove={moveField} onRemove={removeField} onDuplicate={duplicateField} />
                  ))}
                  {(form.fields || []).length === 0 && (
                    <p className="text-center text-xs italic text-neutral-400 py-4">Aucune question — cliquez sur « Ajouter une question » pour commencer.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2 border-t border-neutral-100 pt-4">
              <button type="button" onClick={close} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="admin-form-save">{edit ? "Mettre à jour" : "Créer"}</button>
            </div>
          </form>
        </div>
      )}

      {sendTarget && <SendFormModal form={sendTarget} onClose={() => setSendTarget(null)} />}
      {responsesTarget && <ResponsesModal form={responsesTarget} onClose={() => setResponsesTarget(null)} />}
    </PortalLayout>
  );
}
